import Link from "next/link";
import { useMemo } from "react";
import { Table, Tabs, theme } from "antd";
import { Api } from "@vzbl/integrations";
import { EntityCard } from "src/search/components/EntityCard";
import { useSubscribeEffect } from "src/hooks/useSubscribeEffect";
import { DateRangeSelect } from "src/components/DateRangeSelect";
import { SearchActions } from "src/search/types";
import { useOrg } from "src/hooks/useOrg";
import { EntitySearchContext } from "../useEntityContext";

const PAGESIZE = 5;

export type TabbedFilterItems = {
  name: string;
  context: EntitySearchContext;
};

export function TabbedFilters(props: {
  title?: string;
  items: TabbedFilterItems[];
}) {
  const { token } = theme.useToken();
  const items = props.items.map((item, index) => {
    return {
      key: `${index + 1}`,
      label: item.name,
      children: <SearchResults context={item.context} />,
    };
  });
  return (
    <Tabs
      tabBarExtraContent={{
        left: props.title && (
          <div className="section-header">{props.title}</div>
        ),
      }}
      tabBarStyle={{
        backgroundColor: token.colorBgLayout,
        marginTop: 16,
        marginBottom: 16,
      }}
      style={{ margin: 0, padding: 0 }}
      className="compact-tabs"
      defaultActiveKey="1"
      items={items}
      tabBarGutter={16}
    />
  );
}

function SearchResults(props: { context: EntitySearchContext }) {
  const { entitiesList, params } = props.context;
  const { setPagination } = entitiesList;
  const { token } = theme.useToken();
  const orgId = useOrg().id;
  useSubscribeEffect(entitiesList);
  // TODO figure out how to fetch without doing this.
  if (!entitiesList.isFetched) {
    setPagination({ pageSize: PAGESIZE });
    entitiesList.refetch();
  }
  const data = useMemo(
    () => entitiesList.data?.data || [],
    [entitiesList.data]
  );
  const meta_ = entitiesList.data?.meta;
  const meta = { page: 1, pageSize: PAGESIZE, total: 0, ...meta_ };
  const items: ({ key: string } & Api.EntityItem)[] = useMemo(
    () =>
      data.map(({ attributes }: Api.EntityData) => ({
        key: attributes.uuid,
        entityUuid: attributes.entityUuid,
        ...attributes,
      })),
    [data]
  );
  return (
    <div className="compact-table clickable-rows">
      <DateRangeSelect
        style={{
          width: 174,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          backgroundColor: "transparent",
          textAlign: "left",
          display: "inline-block",
          marginBottom: 16,
        }}
        startDate={params.state.startDate}
        endDate={params.state.endDate}
        duration={params.state.duration || "P30D"}
        onChangeDuration={(duration: any) => {
          params.dispatch({
            type: SearchActions.setDuration,
            value: duration,
          });
        }}
        onChangeRange={(dates: [string, string]) => {
          params.dispatch({
            type: SearchActions.setDates,
            value: dates,
          });
        }}
      />
      <Table
        showHeader={true}
        loading={entitiesList.isLoading}
        dataSource={items}
        pagination={{
          size: "small",
          hideOnSinglePage: true,
          current: meta.page,
          total: meta.total,
          pageSize: meta.pageSize,
          showSizeChanger: false,
          showTotal: (t, [r1, r2]) =>
            (r1 > 1 || r2 < t) && (
              <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                showing {r1}-{r2} of {t}
              </div>
            ),
          onChange: (page, pageSize) => {
            entitiesList.setPagination({ page, pageSize });
            // TODO scroll to top
          },
        }}
        // @ts-ignore-error
        columns={[
          {
            key: "description",
            title: "Description",
            dataIndex: "description",
            ellipsis: true,
            render: (_, record) => (
              <Link
                href={`/${orgId}/entity/${record.entityUuid}`}
                target="_blank"
              >
                <EntityCard {...record} />
              </Link>
            ),
          },
        ]}
      />
    </div>
  );
}
